// WebGL emboss effect using alpha-driven normals (relief shading without strong spec)

export function renderEmboss(sourceCanvas, options = {}, sharedGl) {
  const width = Math.max(1, sourceCanvas.width | 0);
  const height = Math.max(1, sourceCanvas.height | 0);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const gl =
    sharedGl ||
    canvas.getContext('webgl', { premultipliedAlpha: true }) ||
    canvas.getContext('experimental-webgl');
  if (!gl) return sourceCanvas;

  const {
    depth = 1.5, // pixel scale for normal derivation
    lightDir = [-0.4, -0.6, 0.7],
    gain = 0.7, // 0..1 overall strength
    highlight = [1.0, 1.0, 1.0],
    shadow = [0.0, 0.0, 0.0],
  } = options || {};

  const vs = `
        attribute vec2 a_position;
        attribute vec2 a_texCoord;
        varying vec2 v_uv;
        void main(){ v_uv = a_texCoord; gl_Position = vec4(a_position,0.0,1.0); }
    `;
  const fs = `
        precision mediump float;
        varying vec2 v_uv;
        uniform sampler2D u_src;
        uniform vec2 u_texel;
        uniform float u_depthPx;
        uniform vec3 u_lightDir;
        uniform float u_gain;
        uniform vec3 u_highlight;
        uniform vec3 u_shadow;

        vec2 grad(vec2 uv){
            float ax = texture2D(u_src, uv + vec2(u_texel.x, 0.0)).a;
            float bx = texture2D(u_src, uv - vec2(u_texel.x, 0.0)).a;
            float ay = texture2D(u_src, uv + vec2(0.0, u_texel.y)).a;
            float by = texture2D(u_src, uv - vec2(0.0, u_texel.y)).a;
            return vec2(ax - bx, ay - by);
        }

        void main(){
            vec4 base = texture2D(u_src, v_uv);
            float a = base.a;
            vec2 g = grad(v_uv) * u_depthPx;
            vec3 N = normalize(vec3(-g.x, -g.y, 1.0));
            vec3 L = normalize(u_lightDir);
            float ndl = dot(N, L);
            float shade = 0.5 + 0.5 * ndl; // 0..1
            vec3 tint = mix(u_shadow, u_highlight, shade);
            vec3 rgb = mix(base.rgb, base.rgb * tint, u_gain);
            gl_FragColor = vec4(rgb, a);
        }
    `;

  const vert = _createShader(gl, gl.VERTEX_SHADER, vs);
  const frag = _createShader(gl, gl.FRAGMENT_SHADER, fs);
  if (!vert || !frag) return sourceCanvas;
  const prog = _createProgram(gl, vert, frag);
  if (!prog) return sourceCanvas;
  gl.useProgram(prog);

  const pos = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, pos);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
    gl.STATIC_DRAW,
  );
  const uv = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, uv);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([0, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 0]),
    gl.STATIC_DRAW,
  );

  const aPos = gl.getAttribLocation(prog, 'a_position');
  const aUv = gl.getAttribLocation(prog, 'a_texCoord');
  gl.bindBuffer(gl.ARRAY_BUFFER, pos);
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindBuffer(gl.ARRAY_BUFFER, uv);
  gl.enableVertexAttribArray(aUv);
  gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 0, 0);

  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sourceCanvas);

  const uSrc = gl.getUniformLocation(prog, 'u_src');
  const uTexel = gl.getUniformLocation(prog, 'u_texel');
  const uDepth = gl.getUniformLocation(prog, 'u_depthPx');
  const uL = gl.getUniformLocation(prog, 'u_lightDir');
  const uGain = gl.getUniformLocation(prog, 'u_gain');
  const uHl = gl.getUniformLocation(prog, 'u_highlight');
  const uSh = gl.getUniformLocation(prog, 'u_shadow');

  gl.uniform1i(uSrc, 0);
  gl.uniform2f(uTexel, 1 / width, 1 / height);
  gl.uniform1f(uDepth, depth);
  const l = _normalize3(lightDir);
  gl.uniform3f(uL, l[0], l[1], l[2]);
  gl.uniform1f(uGain, Math.max(0, Math.min(1, gain)));
  gl.uniform3f(uHl, highlight[0] || 0, highlight[1] || 0, highlight[2] || 0);
  gl.uniform3f(uSh, shadow[0] || 0, shadow[1] || 0, shadow[2] || 0);

  gl.viewport(0, 0, width, height);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.drawArrays(gl.TRIANGLES, 0, 6);

  return sharedGl ? gl.canvas : canvas;
}

function _createShader(gl, type, src) {
  const s = gl.createShader(type);
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error('TextFX emboss shader error:', gl.getShaderInfoLog(s));
    gl.deleteShader(s);
    return null;
  }
  return s;
}
function _createProgram(gl, vs, fs) {
  const p = gl.createProgram();
  gl.attachShader(p, vs);
  gl.attachShader(p, fs);
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    console.error('TextFX emboss program error:', gl.getProgramInfoLog(p));
    gl.deleteProgram(p);
    return null;
  }
  return p;
}

function _normalize3(v) {
  const x = Number(v && v[0]) || 0;
  const y = Number(v && v[1]) || 0;
  const z = Number(v && v[2]) || 1;
  const len = Math.sqrt(x * x + y * y + z * z) || 1;
  return [x / len, y / len, z / len];
}
